import React, { useEffect, useState } from "react";
import { Card, Form, Button, Row, Col, Image, Alert } from '@themesberg/react-bootstrap';
import { useHistory, useParams } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import defaultIcon from "./../../assets/img/brand/logo2.png";

export default function UserForm() {
    const history = useHistory();
    const { id } = useParams();
    const isEdit = id && id !== 'new';

    // State for each field
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("0");
    const [status, setStatus] = useState("1");
    const [phone, setPhone] = useState("");
    const [martial_status, setMaritalStatus] = useState("");
    const [education_level, setEducationLevel] = useState("");
    const [occupation, setOccupation] = useState("");
    const [interests, setInterests] = useState([]);
    const [lookingFor, setLookingFor] = useState("");
    const [churchName, setChurchName] = useState("");
    const [churchVisit, setChurchVisit] = useState("");
    const [location, setLocation] = useState("");
    const [image, setImage] = useState(null);
    const [existingUri, setExistingUri] = useState("");
    const [saving, setSaving] = useState(false);

    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");

    // Load user data if editing
    useEffect(() => {
        if (isEdit) {
            api.get(`/users/${id}`).then(({ data }) => {
                const u = data.data;
                setName(u.name || "");
                setEmail(u.email || "");
                setGender(u.gender ? String(u.gender) : "0");
                setStatus(u.status ? String(u.status) : "1");
                setPhone(u.phone || "");
                setMaritalStatus(u.martial_status || "");
                setEducationLevel(u.education_level || "");
                setOccupation(u.occupation || "");
                setInterests(u.interests || []);
                setLookingFor(u.looking_for || "");
                setChurchName(u.church_name || "");
                setChurchVisit(u.church_visit || "");
                setLocation(u.location || "");
                // API may return profile_pic or uri; prefer profile_pic
                const pic = u.profile_pic || u.uri || u.image_url;



                // If pic is just a filename, do not assume full URL here — keep value and let preview logic handle it
                setExistingUri(pic ? pic : defaultIcon);
                // setImage(u.uri || null);
            });
        }
    }, [id, isEdit]);
    console.log(existingUri);
    // Validation function
    const validate = () => {
        const newErrors = {};
        if (!name || name.trim() === "") newErrors.name = "Name is required";
        if (!email || email.trim() === "") newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Invalid email";
        if (!gender || gender === "0") newErrors.gender = "Gender is required";
        if (!status || (status !== "1" && status !== "0")) newErrors.status = "Status is required";
        return newErrors;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            setSubmitError("Please fix the errors above.");
            return;
        }
        setSaving(true);
        try {
            if (image instanceof File) {
                const fd = new FormData();
                fd.append('name', name);
                fd.append('email', email);
                fd.append('gender', gender);
                fd.append('status', status);
                fd.append('phone', phone);
                fd.append('martial_status', martial_status);
                fd.append('profile_pic', image);
                fd.append('education_level', education_level);
                fd.append('occupation', occupation);
                fd.append('interests', interests);
                fd.append('looking_for', lookingFor);
                fd.append('church_name', churchName);
                fd.append('church_visit', churchVisit);
                fd.append('location', location);
                if (isEdit) await api.put(`/users/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                else await api.post(`/users`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                const payload = { name, email, gender, status, phone, martial_status, education_level, occupation, interests, looking_for: lookingFor, church_name: churchName, church_visit: churchVisit, location };
                if (isEdit) await api.put(`/users/${id}`, payload);
                else await api.post(`/users`, payload);
            }
            await Swal.fire({ title: 'Success', text: `User ${isEdit ? 'updated' : 'created'} successfully`, icon: 'success', timer: 1500, showConfirmButton: false, heightAuto: false });
            history.push('/users');
        } catch (e) {
            await Swal.fire({ title: 'Error', text: e?.response?.data?.message || e.message || 'Failed to save', icon: 'error', heightAuto: false });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEdit ? 'Edit' : 'Create'} User</h5>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={onSubmit} noValidate>
                    {submitError && <Alert variant="danger">{submitError}</Alert>}
                    <Row>
                        <Col md={8}>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    isInvalid={!!errors.name}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    isInvalid={!!errors.email}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Gender</Form.Label>
                                <Form.Select
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                    isInvalid={!!errors.gender}
                                >
                                    <option value="0">Select Gender</option>
                                    <option value="2">Male</option>
                                    <option value="1">Female</option>
                                    <option value="3">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Marital Status</Form.Label>
                                        <Form.Select value={martial_status} onChange={(e) => setMaritalStatus(e.target.value)}>
                                            <option value="">Select Marital Status</option>
                                            <option value="1">Single</option>
                                            <option value="2">Married</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select
                                            value={status}
                                            onChange={(e) => setStatus(e.target.value)}
                                            isInvalid={!!errors.status}
                                        >
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group id="phone">
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control value={phone} onChange={(e) => setPhone(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Education Level</Form.Label>
                                        <Form.Select value={education_level} onChange={(e) => setEducationLevel(e.target.value)}>
                                            <option value="">Education Level</option>
                                            <option value="1">High School</option>
                                            <option value="2">Graduation</option>
                                            <option value="3">Postgraduate</option>
                                            <option value="4">Doctorate</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group id="occupation">
                                        <Form.Label>Occupation</Form.Label>
                                        <Form.Control value={occupation} onChange={(e) => setOccupation(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Interests</Form.Label>
                                        <Form.Select
                                            multiple
                                            value={interests}
                                            onChange={(e) =>
                                                setInterests(Array.from(e.target.selectedOptions, option => option.value))
                                            }
                                        >
                                            <option value="1">Photography</option>
                                            <option value="2">Shopping</option>
                                            <option value="3">Sing</option>
                                            <option value="4">Pray</option>
                                            <option value="5">Read the Bible</option>
                                            <option value="6">Listen to songs</option>
                                            <option value="7">Run</option>
                                            <option value="8">Swim</option>
                                            <option value="9">Make Art</option>
                                            <option value="10">Travel</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Looking For</Form.Label>
                                        <Form.Select value={lookingFor} onChange={(e) => setLookingFor(e.target.value)}>
                                            <option value="">Looking For</option>
                                            <option value="1">Friendship</option>
                                            <option value="2">Affair</option>
                                            <option value="3">Casamento</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group id="church_name">
                                        <Form.Label>Church Name</Form.Label>
                                        <Form.Control value={churchName} onChange={(e) => setChurchName(e.target.value)} />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Church Visit</Form.Label>
                                        <Form.Select value={churchVisit} onChange={(e) => setChurchVisit(e.target.value)}>
                                            <option value="">Church Visit</option>
                                            <option value="1">Daily</option>
                                            <option value="2">Weekly</option>
                                            <option value="3">Monthly</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group id="location">
                                        <Form.Label>Location</Form.Label>
                                        <Form.Control value={location} onChange={(e) => setLocation(e.target.value)} />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-flex justify-content-end">
                                <Button variant="secondary" className="me-2" onClick={() => history.push('/users')}>Cancel</Button>
                                <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                            </div>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Image</Form.Label>
                                <div style={{ width: '350px' }} className="mb-2">
                                    {image instanceof File ? (
                                        <Image src={URL.createObjectURL(image)} thumbnail />
                                    ) : (
                                        <Image
                                            src={
                                                existingUri && existingUri !== defaultIcon
                                                    ? (existingUri.startsWith('http') ? existingUri : `/uploads/${existingUri}`)
                                                    : defaultIcon
                                            }
                                            onError={(e) => {
                                                if (e.target.src !== defaultIcon) {
                                                    e.target.src = defaultIcon;
                                                }
                                            }}
                                            thumbnail
                                        />
                                    )}
                                </div>
                                {/* <Form.Control type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} /> */}
                            </Form.Group>
                        </Col>
                    </Row>

                </Form>
            </Card.Body>
        </Card>
    );
}